import { Button } from "@dash/form";
import { SelectDialog } from "@dash/select";
import { useMemo, useState } from "react";
import type { FC } from "react";
import { MdAdd } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { SupportTicketAddProductSerialDialog } from "./SupportTicketAddProductSerialDialog";
import { SupportFieldLabel } from "../shared/SupportFieldLabel";
import { SupportTicketProductChip } from "./SupportTicketProductChip";

export type TicketProductDraft = {
  productName: string;
  serialNumber: string | null;
};

type Entry = {
  id: string;
  productName: string;
  serialNumber: string | null;
};

type CoreProps = {
  entries: Entry[];
  onAddProduct: (productName: string, serialNumber: string | null) => void;
  onRemoveEntry: (id: string) => void;
  onSerialChange: (id: string, value: string) => void;
  isDisabled?: boolean;
  label?: string;
};

type PendingProduct = {
  productName: string;
  label: string;
};

const SupportTicketProductsFieldCore: FC<CoreProps> = ({
  entries,
  onAddProduct,
  onRemoveEntry,
  onSerialChange,
  isDisabled,
  label = "Produkty",
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState<PendingProduct | null>(null);
  const docsClient = useServiceClient("docs");

  const selectedProductNameSet = useMemo(() => new Set(entries.map((e) => e.productName)), [entries]);
  const hasProducts = entries.length > 0;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
      <SupportFieldLabel>{label}</SupportFieldLabel>
      <div css={{ display: "flex", flexWrap: "wrap", gap: "7px", alignItems: "center" }}>
        {entries.map((entry) => (
          <SupportTicketProductChip
            key={entry.id}
            productLabel={entry.productName}
            serialNumber={entry.serialNumber}
            isDisabled={isDisabled}
            onRemove={() => onRemoveEntry(entry.id)}
            onSerialChange={(value) => onSerialChange(entry.id, value)}
          />
        ))}
        <Button
          variant={hasProducts ? "text" : "outline"}
          size="small"
          icon={<MdAdd size={hasProducts ? 18 : 16} />}
          onClick={() => setIsPickerOpen(true)}
          isDisabled={isDisabled}
          css={{
            flexShrink: 0,
            ...(hasProducts
              ? {
                  padding: "0.125rem",
                }
              : {}),
          }}
        >
          {hasProducts ? undefined : "Dodaj produkt"}
        </Button>
      {isPickerOpen && (
        <SelectDialog
          variant="single-select"
          label="Wybierz produkt"
          noTrigger
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          search={async (searchText) => {
            const results = await docsClient.searchItems({ searchText, isArchived: false });
            const items = results.value?.items ?? [];
            return items
              .filter((item) => {
                const productName = item.itemName?.trim() || item.productId;
                return productName.length > 0 && !selectedProductNameSet.has(productName);
              })
              .map((item) => {
                const productName = item.itemName?.trim() || item.productId;
                return {
                  label: productName,
                  value: productName,
                };
              });
          }}
          onSelect={(option) => {
            if (!option?.value) return;
            setPendingProduct({ productName: option.value, label: option.label });
            setIsPickerOpen(false);
          }}
        />
      )}
      {pendingProduct && (
        <SupportTicketAddProductSerialDialog
          productLabel={pendingProduct.label}
          isOpen
          onClose={() => setPendingProduct(null)}
          onConfirm={(serialNumber) => {
            onAddProduct(pendingProduct.productName, serialNumber);
            setPendingProduct(null);
          }}
        />
      )}
    </div>
    </div>
  );
};

type CreateProps = {
  value: TicketProductDraft[];
  onChange: (next: TicketProductDraft[]) => void;
  isDisabled?: boolean;
};

export const SupportTicketProductsCreateField: FC<CreateProps> = ({ value, onChange, isDisabled }) => {
  const entries: Entry[] = value.map((p) => ({
    id: p.productName,
    productName: p.productName,
    serialNumber: p.serialNumber,
  }));

  return (
    <SupportTicketProductsFieldCore
      entries={entries}
      onAddProduct={(productName, serialNumber) => {
        if (value.some((p) => p.productName === productName)) return;
        onChange([...value, { productName, serialNumber }]);
      }}
      onRemoveEntry={(id) => onChange(value.filter((p) => p.productName !== id))}
      onSerialChange={(id, serial) =>
        onChange(value.map((p) => (p.productName === id ? { ...p, serialNumber: serial || null } : p)))
      }
      isDisabled={isDisabled}
    />
  );
};

type EditProps = {
  ticketId: Uuid;
  products: TicketDetailDto["products"];
};

export const SupportTicketProductsEditField: FC<EditProps> = ({ ticketId, products }) => {
  const queryClient = useQueryClient();
  const support = useClientManager("support");
  const updateMutation = support.updateTicketProducts().useMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
  });
  const isBusy = "isLoading" in updateMutation ? updateMutation.isLoading : false;

  const currentDrafts: TicketProductDraft[] = (products ?? []).map((p) => ({
    productName: p.productName,
    serialNumber: p.serialNumber,
  }));

  const commit = (next: TicketProductDraft[]) => {
    updateMutation.mutate({ id: ticketId, products: next });
  };

  return (
    <SupportTicketProductsCreateField
      value={currentDrafts}
      onChange={commit}
      isDisabled={isBusy}
    />
  );
};
